import React, { ChangeEvent, RefObject } from 'react';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { FormInstance } from 'antd/lib/form';
import { SelectValue } from 'antd/lib/select';
import { CheckboxChangeEventTarget } from 'antd/lib/checkbox/Checkbox';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { Checkbox, Form, Input, Select } from 'antd';
import { RootState } from 'src/store';
import {
  ICondoReviewBookState,
  setCondoCard,
  setErrorMessageExpirationDate,
  setErrorMessageCardNumber,
  setErrorMessageZipCode,
} from 'src/store/slices/index';
import {
  ICondoCardBooking,
  IIntervalCountriesCodes,
  IRciCountriesCodes,
  SupplierType,
} from 'src/common-types/index';
import {
  checkForEmptyCharacter,
  checkInLatinLetters,
  creatingArrayWithNumber,
  detectCardType,
  hideNumbers,
  insertSpacesInNumber,
  onlyNumsRegExp,
  setValidator,
  getOptionsYear,
  showCardNumber,
  getHiddenFields,
  updateCondoGuest,
  getState,
  isPostalCode,
  hideBrowserAutoFill,
} from 'src/utils/index';
import {
  AmexCardSvg,
  CheckMarkSvg,
  MaestroCardSvg,
  MasterCardSvg,
  VisaCardSvg,
} from 'src/assets/index';
import {
  MONTHS,
  SECOND_CHARACTER_POSITION,
  TENTH_CHARACTER_POSITION,
  CONDO_BOOKING_CARD_INFO_LABEL,
  CONDO_BOOKING_SAME_ADDRESS_INFO_LABEL,
} from 'src/constants/index';
import './style.scss';

interface IMapStateToProps {
  condoReviewBookStore: ICondoReviewBookState;
}

interface IMapDispatchToProps {
  setCondoCard: (card: ICondoCardBooking) => void;
  setErrorMessageExpirationDate: (value: boolean) => void;
  setErrorMessageCardNumber: (value: boolean) => void;
  setErrorMessageZipCode: (value: boolean) => void;
}

interface IProps extends IMapStateToProps, IMapDispatchToProps, WrappedComponentProps {
  formRef: RefObject<FormInstance>;
}

interface IState {
  creditCard: string;
  codeCVV: string;
  expirationMonth: string;
  expirationYear: string;
  isAddressAsPrimary: boolean;
}

const ZERO = 0;
const THREE = 3;
const FOUR = 4;
const MAX_LENGTH = 100;
const MAX_LENGTH_INTERVALS = 25;
const MAX_LENGTH_CITY_FOR_INTERVALS = 15;
const MAX_POSTAL_CODE = 50;
const MIN_LENGTH_CARD_NUMBER = 13;
const MAX_LENGTH_CARD_NUMBER = 23;
const START_HIDE_NUMBER = 5;
const LENGTH_HIDE_NUMBER = 8;

class CondoPaymentMethodComponent extends React.Component<IProps, IState> {
  state: IState = {
    creditCard: '',
    codeCVV: '',
    expirationMonth: undefined,
    expirationYear: undefined,
    isAddressAsPrimary: false,
  };

  onChangeCardName = (e: { target: HTMLInputElement }): void => {
    const { card } = this.props.condoReviewBookStore;
    this.props.setCondoCard(
      updateCondoGuest(card, { holderName: e.target.value }, false) as ICondoCardBooking,
    );
  };

  onChangeCardNumber = (e: { target: HTMLInputElement }): void => {
    const { card } = this.props.condoReviewBookStore;
    const { cardNumber } = card;
    const value = checkForEmptyCharacter(e.target.value);
    const numberCard = insertSpacesInNumber(value);

    if (this.props.condoReviewBookStore.errorsField) {
      this.props.setErrorMessageCardNumber(false);
    }

    if (numberCard !== null) {
      this.props.setCondoCard(
        updateCondoGuest(
          card,
          { cardNumber: numberCard.join('').trim() },
          false,
        ) as ICondoCardBooking,
      );
      this.setState({ creditCard: numberCard.join(' ').trim() });
    } else if (value.length === ZERO) {
      this.props.setCondoCard(
        updateCondoGuest(card, { cardNumber: '' }, false) as ICondoCardBooking,
      );
      this.setState({ creditCard: value });
    } else if (!Number(value)) {
      this.props.setCondoCard(
        updateCondoGuest(card, { cardNumber: cardNumber }, false) as ICondoCardBooking,
      );
      this.setState({ creditCard: cardNumber });
    }
  };

  hideCardNumber = () => {
    const { card } = this.props.condoReviewBookStore;
    const matches = creatingArrayWithNumber(LENGTH_HIDE_NUMBER, START_HIDE_NUMBER);
    const cardType = detectCardType(this.props.condoReviewBookStore.card.cardNumber);

    this.props.setCondoCard(
      updateCondoGuest(card, { cardType: cardType }, false) as ICondoCardBooking,
    );
    this.setState({ creditCard: hideNumbers(this.state.creditCard, matches) });
  };

  onFocusCardNumber = () => {
    this.setState({ creditCard: showCardNumber(this.props.condoReviewBookStore) });
  };

  onChangeMonth = (value: SelectValue) => {
    this.setState({ expirationMonth: value.toString() as string }, () => {
      this.setExpireDate(this.state.expirationYear);
      this.props.formRef.current.setFieldsValue({ months: this.state.expirationMonth });
    });

    if (this.props.condoReviewBookStore.errorsField) {
      this.props.setErrorMessageExpirationDate(false);
    }
  };

  onChangeYear = (value: string) => {
    this.setState({ expirationYear: value }, () => {
      this.setExpireDate(this.state.expirationMonth);
      this.props.formRef.current.setFieldsValue({ years: this.state.expirationYear });
    });

    if (this.props.condoReviewBookStore.errorsField) {
      this.props.setErrorMessageExpirationDate(false);
    }
  };

  setExpireDate = (date: string) => {
    if (!date) {
      return;
    }

    const { card } = this.props.condoReviewBookStore;
    const expireDate = new Date(
      `${this.state.expirationYear}-${this.state.expirationMonth}`,
    ).toISOString();

    this.props.setCondoCard(
      updateCondoGuest(card, { expireDate: expireDate }, false) as ICondoCardBooking,
    );
  };

  onChangeCVV = (e: ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;
    const { card } = this.props.condoReviewBookStore;

    if (onlyNumsRegExp.test(value) || value === '') {
      const value = checkForEmptyCharacter(e.target.value);
      this.props.setCondoCard(updateCondoGuest(card, { cvv: value }, false) as ICondoCardBooking);
      this.setState({ codeCVV: value });
    } else {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  hideCVVCode = () => {
    const { codeCVV } = this.state;
    this.setState({ codeCVV: getHiddenFields(codeCVV) });
  };

  showCVVCode = (): void => {
    const { card } = this.props.condoReviewBookStore;

    if (!card.cvv) {
      return;
    }

    this.setState({ codeCVV: card.cvv });
  };

  onChangeAddressLine1 = (e: { target: HTMLInputElement }): void => {
    const { card } = this.props.condoReviewBookStore;
    this.props.setCondoCard(
      updateCondoGuest(card, { addressLine1: e.target.value }, false) as ICondoCardBooking,
    );
  };

  onChangeAddressLine2 = (e: { target: HTMLInputElement }): void => {
    const { card } = this.props.condoReviewBookStore;
    this.props.setCondoCard(
      updateCondoGuest(card, { addressLine2: e.target.value }, false) as ICondoCardBooking,
    );
  };

  onChangeCity = (e: { target: HTMLInputElement }): void => {
    const { card } = this.props.condoReviewBookStore;
    this.props.setCondoCard(
      updateCondoGuest(card, { city: e.target.value }, false) as ICondoCardBooking,
    );
  };

  onChangeCountry = (value: string): void => {
    const { card, condosCountriesCode, condoBookingSummary } = this.props.condoReviewBookStore;
    const { supplierType } = condoBookingSummary;
    this.props.setCondoCard(
      updateCondoGuest(
        card,
        {
          country: value,
          stateCode: !isEmpty(getState(value, condosCountriesCode, supplierType))
            ? card.stateCode
            : '',
          zipCode: isPostalCode(value, condosCountriesCode, supplierType) ? card.zipCode : '',
        },
        false,
      ) as ICondoCardBooking,
    );
    this.props.formRef.current.setFieldsValue({ country: value });
  };

  onChangePostalCode = (e: ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;
    const { card } = this.props.condoReviewBookStore;

    if (this.props.condoReviewBookStore.errorsField) {
      this.props.setErrorMessageZipCode(false);
    }

    if (onlyNumsRegExp.test(value) || value === '') {
      this.props.setCondoCard(
        updateCondoGuest(card, { zipCode: value }, false) as ICondoCardBooking,
      );
    } else {
      e.preventDefault();
      e.stopPropagation();
      this.props.setCondoCard(
        updateCondoGuest(
          card,
          {
            zipCode: card && card.zipCode ? card.zipCode : '',
          },
          false,
        ) as ICondoCardBooking,
      );
    }
  };

  onChangeState = (value: SelectValue): void => {
    const { card } = this.props.condoReviewBookStore;
    this.props.setCondoCard(
      updateCondoGuest(card, { stateCode: value as string }, false) as ICondoCardBooking,
    );
    this.props.formRef.current.setFieldsValue({ state: value });
  };

  onChangeBillingAddressAsPrimary = (e: { target: CheckboxChangeEventTarget }) => {
    const { card, guest } = this.props.condoReviewBookStore;

    this.props.setCondoCard(
      updateCondoGuest(
        card,
        {
          addressLine1: e.target.checked ? guest.addressLine1 : '',
          addressLine2: e.target.checked ? guest.addressLine2 : '',
          city: e.target.checked ? guest.city : '',
          country: e.target.checked ? guest.country : '',
          stateCode: e.target.checked ? guest.stateCode : '',
          zipCode: e.target.checked ? guest.zipCode : '',
        },
        false,
      ) as ICondoCardBooking,
    );

    this.setState({ isAddressAsPrimary: e.target.checked });

    if (e.target.checked) {
      this.props.formRef.current.setFieldsValue({ addressLine1: guest.addressLine1 });
      this.props.formRef.current.setFieldsValue({ addressLine2: guest.addressLine2 });
      this.props.formRef.current.setFieldsValue({ city: guest.city });
      this.props.formRef.current.setFieldsValue({ country: guest.country });
      this.props.formRef.current.setFieldsValue({ state: guest.stateCode });
      this.props.formRef.current.setFieldsValue({ postalCode: guest.zipCode });
      localStorage.setItem(CONDO_BOOKING_SAME_ADDRESS_INFO_LABEL, 'true');
    } else {
      localStorage.setItem(CONDO_BOOKING_SAME_ADDRESS_INFO_LABEL, null);
    }
  };

  componentDidMount() {
    const { card } = this.props.condoReviewBookStore;
    const cardInfo = localStorage.getItem(CONDO_BOOKING_CARD_INFO_LABEL);
    const isAddressAsPrimary = localStorage.getItem(CONDO_BOOKING_SAME_ADDRESS_INFO_LABEL);
    const defaultData: ICondoCardBooking = {
      addressLine1: '',
      addressLine2: '',
      cvv: '',
      cardNumber: '',
      cardType: null,
      city: '',
      country: '',
      stateCode: '',
      expireDate: '',
      holderName: '',
      phone: '',
      zipCode: '',
    };
    let cardFromStorage;

    this.setState({ isAddressAsPrimary: !!isAddressAsPrimary });

    if (cardInfo) {
      try {
        cardFromStorage = JSON.parse(cardInfo);
      } catch (error) {
        console.error(error);
      }
    }

    const resultData = cardFromStorage ? { ...defaultData, ...cardFromStorage } : defaultData;

    this.props.setCondoCard(updateCondoGuest(card, resultData, false) as ICondoCardBooking);

    if (cardFromStorage) {
      setTimeout(() => {
        this.props.formRef?.current?.setFieldsValue({
          addressLine1: resultData.addressLine1,
          addressLine2: resultData.addressLine2,
          city: resultData.city,
          country: resultData.country,
          state: resultData.stateCode,
          postalCode: resultData.zipCode,
        });
      });
    }
  }

  render(): React.ReactNode {
    const { Option } = Select;
    const { intl } = this.props;
    const { condoReviewBookStore } = this.props;
    const {
      card,
      errorsField,
      condosCountriesCode,
      condoBookingSummary,
      isErrorMessageExpirationDate,
      isErrorMessageCardNumber,
      isErrorMessageZipCode,
    } = condoReviewBookStore;
    const { supplierType } = condoBookingSummary;
    const { creditCard, codeCVV, expirationMonth, expirationYear, isAddressAsPrimary } = this.state;

    return (
      card && (
        <div className="condo-payment">
          <div className="condo-payment__header">
            <h4 className="condo-payment__title">
              <FormattedMessage id="payment.method" />
            </h4>
            <div className="condo-payment__description-wrapper">
              <CheckMarkSvg />
              <p className="condo-payment__description">
                <FormattedMessage id="secure.transmission" />
              </p>
            </div>
            <div className="condo-payment__description-wrapper">
              <CheckMarkSvg />
              <p className="condo-payment__description">
                <FormattedMessage id="personal.information" />
              </p>
            </div>
            <div className="condo-payment__cards">
              <MaestroCardSvg />
              <MasterCardSvg />
              <VisaCardSvg />
              <AmexCardSvg />
            </div>
          </div>
          <div className="condo-payment__rule">
            <Form.Item
              name="holderName"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({ id: 'error.message.name.card' }),
                },
                {
                  pattern: checkInLatinLetters,
                  message: intl.formatMessage({ id: 'error.message.name.card.latin.letters' }),
                },
              ]}
            >
              <div className="condo-payment__input-wrapper">
                <span className="condo-payment__input-label">
                  <FormattedMessage id="name.on.card" />
                </span>
                <div className="condo-payment__input">
                  <Input
                    placeholder={intl.formatMessage({ id: 'first.last.name' })}
                    value={card.holderName}
                    onChange={this.onChangeCardName}
                  />
                </div>
              </div>
            </Form.Item>
            <Form.Item
              name="cardNumber"
              validateTrigger="onBlur"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({ id: 'error.message.card.number' }),
                },
                {
                  min: MIN_LENGTH_CARD_NUMBER,
                  message: intl.formatMessage({ id: 'error.message.card.number.long' }),
                },
                {
                  validator: async () => {
                    await setValidator(errorsField, 'card.cardNumber', isErrorMessageCardNumber);
                  },
                },
              ]}
            >
              <div className="condo-payment__input-wrapper">
                <span className="condo-payment__input-label">
                  <FormattedMessage id="debit.credit.card" />
                </span>
                <div className="condo-payment__input">
                  <Input
                    placeholder={intl.formatMessage({ id: 'card.number' })}
                    maxLength={MAX_LENGTH_CARD_NUMBER}
                    value={creditCard}
                    onChange={this.onChangeCardNumber}
                    onBlur={this.hideCardNumber}
                    onFocus={this.onFocusCardNumber}
                  />
                </div>
              </div>
            </Form.Item>
            <div className="condo-payment__cards-info">
              <Form.Item
                name="months"
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({ id: 'error.message.expiration.date' }),
                  },
                  {
                    validator: async () => {
                      await setValidator(
                        errorsField,
                        'card.expireDate',
                        isErrorMessageExpirationDate,
                      );
                    },
                  },
                ]}
              >
                <div className="condo-payment__input-wrapper">
                  <span className="condo-payment__input-label">
                    <FormattedMessage id="expiration.months" />
                  </span>
                  <div className="condo-payment__input">
                    <Select
                      placeholder={intl.formatMessage({ id: 'months' })}
                      value={expirationMonth}
                      onChange={this.onChangeMonth}
                    >
                      {MONTHS.map((month, index) => (
                        <Option key={index} value={month.number}>
                          {month.number}
                        </Option>
                      ))}
                    </Select>
                  </div>
                </div>
              </Form.Item>
              <div className="condo-payment__cards-info-items">
                <Form.Item
                  name="years"
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({ id: 'error.message.expiration.date' }),
                    },
                    {
                      validator: async () => {
                        await setValidator(
                          errorsField,
                          'card.expireDate',
                          isErrorMessageExpirationDate,
                        );
                      },
                    },
                  ]}
                >
                  <div className="condo-payment__input-wrapper">
                    <span className="condo-payment__input-label">
                      <FormattedMessage id="expiration.year" />
                    </span>
                    <div className="condo-payment__input">
                      <Select
                        placeholder={intl.formatMessage({ id: 'year' })}
                        value={expirationYear}
                        onChange={this.onChangeYear}
                      >
                        {getOptionsYear().map((year, index) => (
                          <Option key={index} value={year}>
                            {year}
                          </Option>
                        ))}
                      </Select>
                    </div>
                  </div>
                </Form.Item>
                <Form.Item
                  name="code"
                  validateTrigger="onBlur"
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({ id: 'error.message.security.code' }),
                    },
                    {
                      min: THREE,
                      message: intl.formatMessage({ id: 'error.message.security.code.long' }),
                    },
                  ]}
                >
                  <div className="condo-payment__input-wrapper">
                    <span className="condo-payment__input-label">
                      <FormattedMessage id="security.code" />
                    </span>
                    <div className="condo-payment__input">
                      <Input
                        maxLength={FOUR}
                        value={codeCVV}
                        onChange={this.onChangeCVV}
                        onBlur={this.hideCVVCode}
                        onFocus={this.showCVVCode}
                      />
                    </div>
                  </div>
                </Form.Item>
              </div>
            </div>
            <div className="condo-payment__billing-address-wrapper">
              <p className="condo-payment__address-title">
                <FormattedMessage id="billing.address" />:
              </p>
              <Checkbox
                onChange={this.onChangeBillingAddressAsPrimary}
                checked={isAddressAsPrimary}
              >
                <FormattedMessage id="billing.address.same.primary" />
              </Checkbox>
              <Form.Item
                name="addressLine1"
                rules={[
                  { required: true, message: intl.formatMessage({ id: 'error.message.address' }) },
                ]}
              >
                <div className="condo-payment__input-wrapper">
                  <span className="condo-payment__input-label">
                    <FormattedMessage id="address.line" values={{ number: 1 }} />
                  </span>
                  <div className="condo-payment__input">
                    <Input
                      placeholder={intl.formatMessage({ id: 'street.and.number' })}
                      maxLength={
                        supplierType === SupplierType.Rci ? MAX_LENGTH : MAX_LENGTH_INTERVALS
                      }
                      value={card.addressLine1}
                      onChange={this.onChangeAddressLine1}
                    />
                  </div>
                </div>
              </Form.Item>
              <Form.Item name="addressLine2" rules={[{ required: false }]}>
                <div className="condo-payment__input-wrapper">
                  <span className="condo-payment__input-label">
                    <FormattedMessage id="address.line" values={{ number: 2 }} />
                  </span>
                  <div className="condo-payment__input">
                    <Input
                      placeholder={intl.formatMessage({ id: 'address.optional' })}
                      maxLength={
                        supplierType === SupplierType.Rci ? MAX_LENGTH : MAX_LENGTH_INTERVALS
                      }
                      value={card.addressLine2}
                      onChange={this.onChangeAddressLine2}
                    />
                  </div>
                </div>
              </Form.Item>
              <Form.Item
                name="city"
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({ id: 'error.message.city' }),
                    pattern: checkInLatinLetters,
                  },
                ]}
              >
                <div className="condo-payment__input-wrapper">
                  <span className="condo-payment__input-label">
                    <FormattedMessage id="city" />
                  </span>
                  <div className="condo-payment__input">
                    <Input
                      placeholder={intl.formatMessage({ id: 'city' })}
                      maxLength={
                        supplierType === SupplierType.Rci
                          ? MAX_LENGTH
                          : MAX_LENGTH_CITY_FOR_INTERVALS
                      }
                      value={card.city}
                      onChange={this.onChangeCity}
                    />
                  </div>
                </div>
              </Form.Item>
              <Form.Item
                name="country"
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({ id: 'error.message.country' }),
                  },
                ]}
              >
                <div className="condo-payment__input-wrapper">
                  <span className="condo-payment__input-label">
                    <FormattedMessage id="country" />
                  </span>
                  <div className="condo-payment__input">
                    <Select
                      showSearch
                      placeholder={hideBrowserAutoFill(
                        intl.formatMessage({ id: 'country' }),
                        SECOND_CHARACTER_POSITION,
                      )}
                      value={card?.country}
                      onChange={this.onChangeCountry}
                    >
                      {supplierType === SupplierType.Rci
                        ? condosCountriesCode?.rciCountries.map(
                            (item: IRciCountriesCodes, index: number) => (
                              <Option key={index} value={item.name}>
                                {item.name}
                              </Option>
                            ),
                          )
                        : condosCountriesCode?.intervalCountries.map(
                            (item: IIntervalCountriesCodes, index: number) => (
                              <Option key={index} value={item.name}>
                                {item.name}
                              </Option>
                            ),
                          )}
                    </Select>
                  </div>
                </div>
              </Form.Item>
              {!isEmpty(getState(card.country, condosCountriesCode, supplierType)) && (
                <Form.Item
                  name="state"
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({ id: 'error.message.state' }),
                      pattern: checkInLatinLetters,
                    },
                  ]}
                >
                  <div className="condo-payment__input-wrapper">
                    <span className="condo-payment__input-label">
                      <FormattedMessage id="state" />
                    </span>
                    <div className="condo-payment__input">
                      <Select
                        showSearch
                        filterOption={(input, option) =>
                          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        value={card?.stateCode || null}
                        placeholder={hideBrowserAutoFill(
                          intl.formatMessage({ id: 'select.state' }),
                          TENTH_CHARACTER_POSITION,
                        )}
                        onChange={this.onChangeState}
                      >
                        {getState(card.country, condosCountriesCode, supplierType).map(
                          (item, index) => {
                            return (
                              <Select.Option key={index} value={item.code}>
                                {item.name}
                              </Select.Option>
                            );
                          },
                        )}
                      </Select>
                    </div>
                  </div>
                </Form.Item>
              )}
              {isPostalCode(card.country, condosCountriesCode, supplierType) && (
                <Form.Item
                  name="postalCode"
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({ id: 'error.message.postal.code' }),
                    },
                    {
                      validator: async () => {
                        await setValidator(errorsField, 'guests[0]', isErrorMessageZipCode);
                      },
                    },
                  ]}
                >
                  <div className="condo-payment__input-wrapper postal-code-input">
                    <span className="condo-payment__input-label">
                      <FormattedMessage id="postal-code" />
                    </span>
                    <div className="condo-payment__input">
                      <Input
                        placeholder={intl.formatMessage({ id: 'postal-code' })}
                        maxLength={MAX_POSTAL_CODE}
                        onChange={this.onChangePostalCode}
                        value={card.zipCode}
                      />
                    </div>
                  </div>
                </Form.Item>
              )}
            </div>
          </div>
        </div>
      )
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    condoReviewBookStore: state.condoReviewBookStore,
  };
};

const mapDispatchToProps: IMapDispatchToProps = {
  setCondoCard,
  setErrorMessageExpirationDate,
  setErrorMessageCardNumber,
  setErrorMessageZipCode,
};

export const CondoPaymentMethod = connect(
  mapStateToProps,
  mapDispatchToProps,
)(injectIntl(CondoPaymentMethodComponent));
